import { UPCOMING_GAMES_LABEL } from 'constants';
import React, { memo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Mousewheel, Virtual } from 'swiper/modules';
import { isEmptyObject } from 'helpers';
import { useTeamColors } from 'hooks';

import GameDate from 'components/GameDate';
import {
  GameInfoButton,
  Teams,
  TvChannel,
  GameResult,
  LeagueName,
} from 'components';
import { CurrentGameButton } from './components/CurrentGameButton';
import { PrevArrowIcon, NextArrowIcon } from 'assets/icons';

import 'swiper/swiper-bundle.css';
import {
  StyledSection,
  LeftSideBar,
  RightSideBar,
  SlideContent,
  ArrowWrapper,
} from './style';

/**
 * Dashboard component that displays various game-related information and user preferences.
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.games - List of games to display
 * @param {Object} props.currentGame - Current game object
 * @param {Function} props.setEventLeague - Function to set the event league
 * @returns {React.ReactElement} Dashboard view with upcoming games, championships, and custom events
 * @example
 * ```jsx
 * <Dashboard games={[]} currentGame={{}} setEventLeague={() => {}} />
 * ```
 */
const Dashboard = ({ games, currentGame, setEventLeague }) => {
  const { data: configuration } = useSelector((state) => state.configuration);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);
  const { mainColor } = useTeamColors();
  const leagueFromUrl = searchParams.get('league');
  const eventIdFromUrl = searchParams.get('eventId');
  const swiperRef = useRef(null);

  const isNotSelectionMade =
    (configuration.display_leagues_select === '1' ||
      configuration.display_leagues_select === 1) &&
    isEmptyObject(params);

  // Calculate initial slide: use eventId from URL if present, otherwise use currentGame
  const getInitialSlide = () => {
    if (eventIdFromUrl && games?.length) {
      const gameIndex = games.findIndex(
        (game) => game.event_id === Number(eventIdFromUrl)
      );
      return gameIndex !== -1 ? gameIndex : currentGame;
    }
    return currentGame;
  };

  const initialSlide = getInitialSlide();

  //** FIND event_id FUNCTION TO SET IN URL PARAM **//
  const slideChanged = useCallback(
    (index) => {
      if (!games?.length) return;
      const game = games[index];
      const eventLeagueName = game?.event_league_name;
      if (eventLeagueName && setEventLeague) {
        setEventLeague(eventLeagueName);
        window.dispatchEvent(
          new CustomEvent('eventLeagueChanged', {
            detail: { league: eventLeagueName },
          })
        );
      }
      // Update eventId in URL for CurrentGameButton
      if (!isNotSelectionMade && game?.event_id) {
        setSearchParams(
          (prevValue) => {
            prevValue.set('eventId', game.event_id);
            return prevValue;
          },
          { replace: true }
        );
      }
    },
    [games, setEventLeague, isNotSelectionMade, setSearchParams]
  );

  // Set eventId in URL params for team/league pages (not Today's Events)
  // Only update from Redux if eventId is not already set in URL
  useEffect(() => {
    if (
      !isNotSelectionMade &&
      !eventIdFromUrl &&
      games?.[currentGame]?.event_id
    ) {
      const eventId = games[currentGame].event_id;
      const game = games[currentGame];

      console.log('[Dashboard] Setting eventId in URL:', {
        eventId,
        currentGame,
        gameDetails: {
          start: game.event_start,
          away: game.event_away_team_name,
          home: game.event_home_team_name,
        },
      });

      setSearchParams(
        (prevValue) => {
          prevValue.set('eventId', eventId);
          return prevValue;
        },
        { replace: true }
      );
    }
  }, [isNotSelectionMade, currentGame, games, setSearchParams, eventIdFromUrl]);

  const isUpcomingGames =
    (configuration.display_teams_select === '1' ||
      configuration.display_teams_select === 1) &&
    params.team === UPCOMING_GAMES_LABEL;

  useEffect(() => {
    if (games?.length && !isUpcomingGames) {
      let event = games[0];
      if (event?.event_league_name && setEventLeague) {
        setEventLeague(event.event_league_name);
      }
    }
  }, [isUpcomingGames, games, setEventLeague]);

  // When eventIdFromUrl changes (e.g., from clicking a news link), programmatically scroll Swiper to that slide
  useEffect(() => {
    if (eventIdFromUrl && games?.length && swiperRef.current) {
      const gameIndex = games.findIndex(
        (game) => game.event_id === Number(eventIdFromUrl)
      );
      if (gameIndex !== -1 && swiperRef.current.swiper) {
        swiperRef.current.swiper.slideTo(gameIndex, 0); // 0ms transition for instant movement
      }
    }
  }, [eventIdFromUrl, games]);

  return (
    <>
      <LeftSideBar>
        <ArrowWrapper className="swiper-button-prev-unique">
          <PrevArrowIcon fill={mainColor} />
        </ArrowWrapper>
      </LeftSideBar>
      <StyledSection>
        <Swiper
          ref={swiperRef}
          id="main-slider"
          initialSlide={initialSlide}
          modules={[Navigation, Mousewheel, Virtual]}
          mousewheel={true}
          virtual={{
            enabled: true,
            addSlidesAfter: 1,
            addSlidesBefore: 1,
          }}
          observer={true}
          observeParents={true}
          navigation={{
            prevEl: '.swiper-button-prev-unique',
            nextEl: '.swiper-button-next-unique',
          }}
          onSlideChange={(swiper) => {
            slideChanged(swiper.activeIndex);
          }}
          slidesPerView={1}
          tag="section"
          wrapperTag="ul"
        >
          {games.map((game, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SwiperSlide
              key={`${game.event_title || game.team_a_event_espn_id || index}`}
              tag="li"
            >
              <SlideContent>
                <Teams
                  eventType={game?.event_type}
                  location={game.event_location}
                  opponent={
                    isUpcomingGames || isNotSelectionMade
                      ? game.team_b_event_text
                      : game.event_text || game.team_b_event_text
                  }
                  team={
                    isUpcomingGames || isNotSelectionMade
                      ? game.team_a_event_text
                      : params?.team
                  }
                />
                {game?.event_league_name && game?.event_league_name != '' ? (
                  <LeagueName leagueName={game?.event_league_name} />
                ) : null}
                {game.stadium_address || game.event_location_id ? (
                  <GameInfoButton
                    city={game.location_city}
                    date={game.event_start}
                    eventLeagueName={game.event_league_name || leagueFromUrl}
                    eventType={game?.event_type}
                    locationId={game.event_location_id}
                    opponent={
                      isUpcomingGames || isNotSelectionMade
                        ? game.team_b_event_text
                        : game.event_text || game.team_b_event_text
                    }
                    region={game.stadium_country}
                    stadium={game.stadium_name}
                    stadiumLocation={{
                      lat: game?.stadium_lat,
                      lng: game?.stadium_lng,
                    }}
                    team={
                      isUpcomingGames || isNotSelectionMade
                        ? game.team_a_event_text
                        : params?.team
                    }
                    timeZone={game.stadium_time_zone}
                    zipCode={game.stadium_zip_code}
                    channel={game.event_tv_channel}
                    eventState={game.event_state}
                    headline={game.event_notes_headline}
                  />
                ) : null}
                <GameDate
                  eventState={game.event_state}
                  eventTime={game.event_start}
                  timeZone={game.stadium_time_zone}
                />
                <TvChannel channel={game.event_tv_channel} />
                <GameResult
                  date={game.event_start}
                  eventNotesHeadline={game.event_notes_headline}
                  eventState={game.event_state}
                  result={game.event_outcome}
                />
                {!isUpcomingGames ? <CurrentGameButton /> : null}
              </SlideContent>
            </SwiperSlide>
          ))}
        </Swiper>
      </StyledSection>
      <RightSideBar>
        <ArrowWrapper className="swiper-button-next-unique">
          <NextArrowIcon fill={mainColor} />
        </ArrowWrapper>
      </RightSideBar>{' '}
    </>
  );
};

export default memo(Dashboard);
